import * as React from 'react';
import styles from './SectionCard.module.css';

export interface SectionCardProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  // Also the accessible name of the collapse control, so it must be plain text.
  title: string;
  // Sits before the title. The card places it but does not style it — chip chrome, colours and
  // sizing belong to the caller.
  icon?: React.ReactNode;
  // Sits directly after the title, still left-aligned: counts, "3 changed" markers and the like.
  badges?: React.ReactNode;
  // Pushed to the right of the header bar. Clicks inside it do not toggle the card.
  headerRight?: React.ReactNode;
  // Pass both to make the card collapsible; omit them for a card that is always open.
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  // Applied alongside the card's own body class, for callers that need to style what they put
  // inside (grid gaps, stacking) without reaching into this module.
  bodyClassName?: string;
  children: React.ReactNode;
}

const cx = (...names: (string | false | undefined)[]) => names.filter(Boolean).join(' ');

// Card chrome shared by the resident settings form and the device config form. Presentation only:
// it owns the border, the banded header and the collapse behaviour, and knows nothing about what
// it contains.
// TODO: Move to @ixicare/ui once the API has settled across both callers.
const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon,
  badges,
  headerRight,
  collapsed = false,
  onToggleCollapsed,
  bodyClassName,
  className,
  children,
  ...rest
}) => {
  const collapsible = Boolean(onToggleCollapsed);

  return (
    <section
      className={cx(styles.section, collapsible && styles.collapsible, collapsed && styles.collapsed, className)}
      {...rest}
    >
      <div className={styles.sectionHead} onClick={onToggleCollapsed}>
        {icon && <span className={styles.icon}>{icon}</span>}
        <h2>{title}</h2>
        {badges}
        {headerRight && (
          <div className={styles.headerRight} onClick={(e) => e.stopPropagation()}>
            {headerRight}
          </div>
        )}
        {collapsible && (
          <button
            type="button"
            className={styles.chev}
            // The whole header toggles, and this sits inside it, so a click here would otherwise
            // run the handler twice and land back where it started.
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapsed?.();
            }}
            // Named after the section it toggles, so it reads as "Geofencing, collapsed" rather
            // than needing its own translated label.
            aria-label={title}
            aria-expanded={!collapsed}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        )}
      </div>
      <div className={cx(styles.sectionBody, bodyClassName)}>{children}</div>
    </section>
  );
};

export default SectionCard;
